import { InputField, InputFieldProps } from '@rocketlane/ui';
import { InputProps, InputRef } from 'antd/es/input';
import useFormInstance from 'antd/lib/form/hooks/useFormInstance';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Input as AntdInput } from 'components/atoms/input';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';

type AntdInputProps = Omit<InputProps, 'size'> & {
  size?: 'small' | 'middle' | 'default' | 'large';
};

function mapAntdPropsToRadixProps(
  props: AntdInputProps,
): Omit<InputFieldProps, 'ref'> {
  const {
    size,
    bordered: _bordered, // eslint-disable-line @typescript-eslint/no-unused-vars
    prefix,
    suffix,
    ...restProps
  } = props;

  // Map Antd size values to Radix size values
  let mappedSize: 'small' | 'medium' | 'large' | undefined;

  if (size === 'small') {
    mappedSize = 'small';
  } else if (size === 'large') {
    mappedSize = 'large';
  } else if (size === 'middle') {
    mappedSize = 'medium';
  } else if (size === 'default') {
    mappedSize = 'medium';
  } else {
    mappedSize = 'medium'; // default fallback
  }

  const startAddons = prefix ? [prefix] : undefined;
  const endAddons = suffix ? [suffix] : undefined;

  return {
    ...restProps,
    size: mappedSize,
    startAddons,
    endAddons,
  };
}

interface InputComponent
  extends React.ForwardRefExoticComponent<
    AntdInputProps & React.RefAttributes<InputRef>
  > {
  Group: typeof AntdInput.Group;
  Search: typeof AntdInput.Search;
  TextArea: typeof AntdInput.TextArea;
  Password: typeof AntdInput.Password;
}

const useFormAwareSizeProps = (props: Omit<InputFieldProps, 'ref'>) => {
  const form = useFormInstance();
  const insideForm = !!form;
  const { size, ...restProps } = props;
  const isSizeUndefined = !size;
  const updatedSize = insideForm && isSizeUndefined ? 'large' : size;

  return {
    ...restProps,
    size: updatedSize,
  };
};

const RocketlaneInput = React.forwardRef<
  HTMLInputElement,
  Omit<InputFieldProps, 'ref'>
>((props, ref) => {
  const updatedProps = useFormAwareSizeProps(props);

  return <InputField {...updatedProps} ref={ref} />;
});

const InputComponent = React.forwardRef<InputRef, AntdInputProps>(
  (props, ref) => {
    const isNewDesignEnabled = useAntdRlUISwitcher();

    // Create ref callback outside conditional to satisfy React hooks rules
    const radixRef = useCallback(
      (element: HTMLInputElement | null) => {
        if (!ref) {
          return;
        }

        const refElement = element as unknown as InputRef;

        if (typeof ref === 'function') {
          ref(refElement);
        } else {
          (ref as React.MutableRefObject<InputRef | null>).current = refElement;
        }
      },
      [ref],
    );

    if (isNewDesignEnabled) {
      const radixProps = mapAntdPropsToRadixProps(props);

      // eslint-disable-next-line react/jsx-props-no-spreading
      return <RocketlaneInput {...radixProps} ref={radixRef} />;
    }

    // Convert "default" size to "middle" for AntdInput
    const antdProps = {
      ...props,
      size: props.size === 'default' ? 'middle' : props.size,
    } as InputProps;

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <AntdInput {...antdProps} ref={ref} />;
  },
) as InputComponent;

InputComponent.displayName = 'InputComponent';

// Create the facade Input as a styled component to enable ${Input} interpolation
// This extends the original AntdInput which already works with styled-components
export const Input = styled(InputComponent)`` as typeof AntdInput;

// Add static properties to match AntdInput interface
Input.Group = AntdInput.Group;
Input.Search = AntdInput.Search;
Input.TextArea = AntdInput.TextArea;
Input.Password = AntdInput.Password;
Input.displayName = 'Input';
export { TextArea } from 'components/facade/textarea';

export { InputNumber, SearchInput } from 'components/atoms/input';
